/**
 * Analyze Current Stack copy — every string this flow renders, in one module.
 *
 * Same convention as `Dashboard/copy.js`: strings live here rather than inline
 * in JSX so `copy.test.js` can assert the whole surface in one place, and so a
 * ruled string is diffable against the ruling instead of being scattered
 * through components nobody reads together.
 *
 * Voice rules (inherited from the dashboard's §9): sentence case except
 * eyebrow labels; no emoji; never the words "proven", "guaranteed", "safe" or
 * "working". "Membership", never "Pro" (repo `CLAUDE.md`).
 *
 * **Two kinds of string live here and they are not interchangeable.** RULED
 * strings were decided by Tanu and carried into STACK-544 verbatim; do not
 * edit them for tone or length. PROPOSED strings are this run's wording for a
 * line the ticket explicitly reserved ("propose, don't decide") — they ship so
 * the surface is complete and are Tanu's to approve or replace before merge.
 */

// ---------------------------------------------------------------------------
// Entry — the New stack menu
// ---------------------------------------------------------------------------

/** RULED: the menu item's label. The feature's name, unchanged since 2026-08. */
export const MENU_ITEM_TITLE = 'Analyze Current Stack';

/**
 * RULED (Tanu, 2026-09-25): kept as proposed in STACK-544.
 *
 * The line this replaces read "Check your current routine for potential
 * interactions and supplement insights", which described the retired feature:
 * interactions were most of what it did. v1 is a research lookup against one
 * goal — what the studies used, and how the member's own amounts compare — and
 * interactions are a check that already runs on every stack, analysed or not.
 * Promising them here would sell the part that is not new.
 *
 * Alternatives considered, in the PR body: "See what the research says about
 * the supplements you already take" (shorter, drops the goal, and the goal is
 * the thing the analysis is *against*), and "Compare your supplements against
 * the studies for one health goal" (accurate but reads like a form
 * instruction).
 */
export const MENU_ITEM_DESCRIPTION =
  'See what the research says about the supplements you already take, for one health goal';

/** RULED: the sibling item, unchanged. Restored verbatim from before 2026-09-12. */
export const AI_MENU_ITEM_TITLE = 'Create with myStack AI';
export const AI_MENU_ITEM_DESCRIPTION =
  'Get a personalized, evidence-based supplement stack, built around your health goals';

// ---------------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------------

/**
 * RULED: the CTA. Was "Analyze with myStack AI" (STACK-544's Scope); renamed
 * by Tanu's Analyze Stack engineering handoff, 2026-09-24.
 */
export const SUBMIT_CTA = 'Analyze';

/**
 * RULED (handoff, 2026-09-24): the page title, in sentence case. Deliberately
 * not `MENU_ITEM_TITLE` — the menu item keeps its title-case name; only the
 * page heading changed.
 */
export const PAGE_TITLE = 'Analyze current stack';

/** RULED (handoff, 2026-09-24): the stack-name field's label and example. */
export const STACK_NAME_LABEL = 'Stack name';
export const STACK_NAME_PLACEHOLDER = 'e.g. Evening calm';

/** RULED (handoff, 2026-09-24): the supplements section heading. */
export const SUPPLEMENTS_HEADING = 'Your supplements';

/**
 * RULED (handoff, 2026-09-24): the empty list. Tells the member what to enter,
 * where the generic table says only that nothing is there yet.
 */
export const EMPTY_LIST_PROMPT = 'Add each supplement you take, with your dose.';

/**
 * RULED (handoff, 2026-09-24): the two halves of the split dose chip — what
 * the member entered, and what the clinical studies used.
 */
export const YOUR_DOSE_LABEL = 'Your dose';
export const STUDIED_DOSE_LABEL = 'Studied dose';

/**
 * RULED (Aravind, 2026-10-01, STACK-524): a studied dose above the
 * supplement's signed safety limit is not shown as the dose to compare
 * against; the backend replaces it with the signed standard dose. The chip's
 * half is relabelled so a standard dose never reads as a study's, and the line
 * under the row says why, quoting the studied dose so nothing is hidden.
 */
export const STANDARD_DOSE_LABEL = 'Standard dose';
export const safetyLimitNote = (studiedDoseText) =>
  `The studied dose (${studiedDoseText}) is above the safe daily limit, so the standard dose is shown.`;

/**
 * RULED (handoff, 2026-09-24): the hint beside a disabled Analyze, naming what
 * is missing. `SUPPLEMENTS_REQUIRED` below is the third case, and already
 * reads exactly as the handoff's.
 */
export const ANALYZE_HINT_NEEDS_BOTH = 'Add a goal and at least one supplement to analyze.';
export const ANALYZE_HINT_NEEDS_GOAL = 'Add a goal to analyze.';

/**
 * RULED (Tanu, 2026-09-25), in the same slot as the Analyze hints above.
 *
 * The same hint on the Save step: Save stays disabled until the stack has a
 * name, and without this line it was disabled with nothing saying why — the
 * placeholder ("e.g. Evening calm") reads like a name already filled in
 * (Tanu, 2026-09-25).
 */
export const SAVE_HINT_NEEDS_NAME = 'Name your stack to save.';

/**
 * PROPOSED, for Tanu to approve on staging (2026-10-04): the stack name is
 * required to save, and the field now says so. Save stays pressable; pressed
 * without a name, it puts the cursor in the field and shows this under it,
 * where a greyed-out button said nothing about why.
 */
export const STACK_NAME_REQUIRED = 'Add a name to save your stack.';

/**
 * PROPOSED, for Tanu to approve on staging (2026-10-04): the list is locked
 * while an analysis runs, and this replaces the add control meanwhile.
 * Anything added mid-run used to be left out silently (staging case C4).
 */
export const LIST_LOCKED_WHILE_ANALYZING = 'You can edit your list when the analysis finishes.';

/**
 * PROPOSED, for Tanu to approve on staging (2026-10-04): each row's analysis
 * state, so an empty score ring means one thing. A row analysed with no
 * studies and a row never analysed drew the same "—" ring.
 */
export const ROW_STATE_ANALYZING = 'Analyzing…';
export const ROW_STATE_NOT_ANALYZED = 'Not analyzed yet. Analyze again to include it.';
export const ROW_STATE_NO_STUDIES = 'No studies found for this goal.';
export const ROW_STATE_NOT_IN_LIBRARY = 'Not in our research library yet.';

/**
 * The accessible name of the goals field's own submit arrow.
 *
 * Deliberately NOT `SUBMIT_CTA`. The arrow and the footer button run the same
 * action, and giving them the same name puts two identically-named buttons on
 * one page — a screen-reader user tabbing through hears the same control
 * twice and cannot tell which they are on. Different names, one action, which
 * is the accessible shape of "press Enter or press the button".
 */
export const COMPOSER_SUBMIT_LABEL = 'Analyze my stack';

/** The CTA while the analysis runs — the generator's `GENERATING` in this flow's words. */
export const ANALYZING = 'Analyzing your stack…';

/**
 * The section label above the goals field.
 *
 * There is deliberately **no placeholder string here.** The field is ruled to
 * be identical to Create with myStack AI, and it is that component
 * (`common/StackComposer`), which rotates its own `e.g. "..."` examples. A
 * placeholder in this module would be a second, unused copy that read as the
 * truth about a surface it does not drive.
 */
export const GOALS_LABEL = 'Health goals';

/** Shown when the member asks for an analysis with no goal named. */
export const GOALS_REQUIRED = 'A health goal is required to analyze your stack.';

/** Shown when the member asks for an analysis with nothing in the list yet. */
export const SUPPLEMENTS_REQUIRED = 'Add at least one supplement to analyze.';

/**
 * Shown when an analysis is refused because a **stack generation** is already
 * running — one app-wide job slot serves both flows.
 *
 * It exists because the alternative is silence: this form's progress panel
 * only draws for an analysis, so a member blocked by a generation would press
 * Analyze and see nothing happen at all. Names the other flow rather than
 * saying "busy", because the member started it and can go and watch it.
 */
export const ANALYSIS_BUSY_GENERATING =
  'Your stack is still being built. Wait for that to finish, then analyze.';

/**
 * The same refusal when the job in the way is an analysis of a *different*
 * stack — started on another stack's edit page, or on the analyze page while
 * this is an edit page. It draws no progress here for the same reason a
 * generation does not, so it needs its own line for the same reason too.
 */
export const ANALYSIS_BUSY_ANALYZING =
  'Another stack is still being analyzed. Wait for that to finish, then try again.';

/**
 * Shown when the supplement list changed while the analysis was running.
 *
 * The result is merged onto the member's **current** rows, because throwing
 * away edits they made while waiting would be worse. But that means a
 * supplement added mid-analysis carries no results — and a row with no
 * results is visually identical to one the research had nothing to say about.
 * Without this line the member cannot tell those apart, and would read a
 * partial analysis as a complete one.
 */
export const ANALYSIS_LIST_CHANGED =
  'Some supplements changed while this ran, so they are not included. Analyze again to cover them.';

/** The failure line. Says what to do, and does not lose the member's list. */
export const ANALYSIS_FAILED =
  'We could not analyze your stack just now. Your supplements are still here — please try again.';

// ---------------------------------------------------------------------------
// Results — the row actions
// ---------------------------------------------------------------------------

/**
 * RULED: the two row actions, per `entryState`.
 *
 * `add_amount` — the member entered a count ("1 tablet"), which is not an
 * amount, so there is nothing to compare a study dose against.
 * `check_amount` — the member entered an amount that is implausible for the
 * unit ("1000 g"), so comparing it would print a ratio nobody should read.
 *
 * Both open the existing Edit dose flow, and neither row shows a study
 * comparison until the amount is fixed.
 *
 * Since Tanu's "1c" mock (2026-09-24) each sits as the single button at the
 * end of a one-line notice under the chips, and the implausible-amount action
 * reads "Change amount" (was "Check amount") — it names what the member does.
 */
export const ADD_AMOUNT = 'Add amount';
export const CHECK_AMOUNT = 'Change amount';

/**
 * RULED (Tanu, 2026-09-25): the notice for an implausible amount, quoting the
 * member's own amount — "1,000 g is more than a daily dose could be. Check the
 * weight, e.g. mg."
 *
 * Replaces "1,000 g is far above any studied dose.", which claimed a
 * comparison with the studies that the check never makes: `check_amount` is a
 * fixed ceiling per unit (backend `IMPLAUSIBLE_DOSE_CEILING`), the same for
 * every supplement, and in practice catches unit slips such as g for mg.
 * "Check the weight" rather than "Check the unit", because a member does not
 * know what "unit" refers to.
 */
export const checkAmountMessage = (amountText) =>
  `${amountText} is more than a daily dose could be. Check the weight, e.g. mg.`;

/**
 * RULED ("1c", 2026-09-24): the notice for a count rather than an amount,
 * naming the unit the studies use — "Studies report doses in mg."
 *
 * RULED (Tanu, 2026-09-24): `ADD_AMOUNT_FALLBACK`, for a row whose unit the
 * studies cannot supply: studies were found but none reported a usable dose,
 * so there is no study dose to read a unit from. The supplement catalog
 * carries no default unit, so the line names the kind of amount rather than a
 * guessed unit. (With no studies at all there is no notice — see
 * `entryStateAction` in `doseViews.js`.)
 */
export const ADD_AMOUNT_FALLBACK = 'Studies report doses in weight, e.g. mg.';
export const addAmountMessage = (unit) =>
  (unit ? `Studies report doses in ${unit}.` : ADD_AMOUNT_FALLBACK);

/** Entry states that carry an action instead of a study comparison. */
export const ACTION_FOR_ENTRY_STATE = {
  add_amount: ADD_AMOUNT,
  check_amount: CHECK_AMOUNT,
};

// ---------------------------------------------------------------------------
// Results — the muted slot under the list
// ---------------------------------------------------------------------------

/**
 * RULED: the two lines this flow adds under the supplement list, and their
 * order. They sit after the existing interaction line (`SafetyNote`), which
 * this ticket does not change.
 *
 * **A clean result shows none of them.** Each renders only when its own
 * condition holds — see `analysisNoteFlags` in `mergeAnalysis.js`. A line
 * that always renders is a line nobody reads, and one of these says something
 * is missing from the analysis; printing that when nothing is missing is worse
 * than printing nothing.
 */
export const MERGED_AMOUNTS_NOTE =
  'Amounts are combined for supplements entered more than once.';

export const NOT_IN_LIBRARY_NOTE =
  "Supplement studies not in our research library aren't included.";

/**
 * RULED (Tanu, 2026-09-25): a third line, for supplements the analysis found
 * no studies on for this goal. Without it a row simply had no studied dose
 * and an empty ring, and nothing said why. Says "found" rather than "there are
 * no studies": retrieval reads the top matches for the goal, so a supplement
 * can have studies in the library that this goal's search did not reach.
 * Placed after the two earlier lines so their order is untouched.
 */
export const NO_STUDIES_NOTE = 'No studies found for this goal.';

/**
 * The order of the muted slot's lines: the two ruled on 2026-09-24, then the
 * no-studies line added after them on 2026-09-25.
 *
 * `AnalysisNotes` renders them in this order directly rather than mapping over
 * this array — each line is conditional on a different flag, so a map would
 * need the flags threaded through it to no benefit. The array exists so
 * `copy.test.js` has something to assert the order against; a test that
 * restated the order would just be a second copy of the ruling.
 */
export const ANALYSIS_NOTES_IN_ORDER = [MERGED_AMOUNTS_NOTE, NOT_IN_LIBRARY_NOTE, NO_STUDIES_NOTE];

/**
 * A muted line with the supplements it applies to (Tanu, 2026-09-24): "Amounts
 * are combined for supplements entered more than once: Vitamin D." The ruled
 * sentence is kept verbatim and the names follow it, so the wording decision
 * stands and only the pointer is added. With no names, the line is unchanged.
 */
export const withSupplementNames = (line, names) =>
  (Array.isArray(names) && names.length
    ? `${line.replace(/\.$/, '')}: ${names.join(', ')}.`
    : line);
